import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, buttonClass } from "../components/Bits";

const JOB_LABELS = {
  "sequences.advance": "Daily cadence advance",
  "digest.weekly": "Monday desk summary",
  "renewals.enrol": "Monthly pre-renewal enrolment",
};

const RunLine = ({ job, run }) => (
  <li className="flex flex-wrap items-center justify-between gap-3 py-2.5"
      data-testid={`automation-job-${job.replace(".", "-")}`}>
    <p className="text-sm text-cream/85">{JOB_LABELS[job] || job}</p>
    <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${
      !run ? "text-cream/30" : run.status === "complete" ? "text-sage"
        : run.status === "failed" ? "text-terra" : "text-gold"}`}>
      {run ? `${run.status} · ${new Date(run.started_at).toLocaleString()}` : "not run yet"}
    </span>
  </li>
);

export const AutomationPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () =>
    adminApi.get("/crm/automation/status").then(({ data }) => setStatus(data)).catch(() => {});

  useEffect(() => { load(); }, []);

  if (!status) return null;

  const run = async (path, label) => {
    setBusy(path);
    try {
      const { data } = await adminApi.post(path);
      toast.success(data.enrolled != null
        ? `${data.enrolled} enrolled for ${data.renewal_month}`
        : data.sent ? `${label} sent to ${data.to}`
        : data.reason || `${label} could not be delivered`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <Panel title="Running on its own" testid="panel-automation">
      <p className="text-sm leading-relaxed text-cream/55" data-testid="automation-summary">
        The cadence queue refills every morning. The desk summary reaches you on{" "}
        {status.digest_schedule}. On the first of each month the top{" "}
        {status.renewal_limit} accounts renewing next month go into
        &ldquo;{status.renewal_cadence}&rdquo;.
      </p>

      <ul className="mt-5 divide-y divide-cream/10">
        {Object.entries(status.jobs).map(([job, lastRun]) => (
          <RunLine key={job} job={job} run={lastRun} />
        ))}
      </ul>

      {!status.owner_email_set ? (
        <p className="mt-4 text-xs text-terra" data-testid="automation-no-email">
          No owner email is configured, so the summary cannot be delivered.
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button type="button" className={buttonClass} data-testid="automation-send-digest"
                disabled={busy !== ""}
                onClick={() => run("/crm/automation/digest/send", "Desk summary")}>
          {busy.includes("digest") ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Send the summary now
        </button>
        <button type="button" data-testid="automation-run-renewals" disabled={busy !== ""}
                onClick={() => run("/crm/automation/renewals/run", "Renewal enrolment")}
                className="inline-flex items-center gap-2 rounded-full border border-cream/15 px-4 py-2 text-xs text-cream/70 transition-colors duration-200 hover:border-gold/50 hover:text-cream">
          {busy.includes("renewals") ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Enrol next month's renewals
        </button>
      </div>
    </Panel>
  );
};
